import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import type { TestPlan } from "@/services/testing";

export function planVerification(plan: TestPlan): {
	verified: boolean;
	passed: number;
	total: number;
	flaky?: boolean;
	at: string;
} | null {
	const steps = plan.steps as unknown;
	if (steps && typeof steps === "object" && !Array.isArray(steps)) {
		const v = (steps as { verification?: unknown }).verification;
		if (v && typeof v === "object" && "verified" in v) {
			return v as { verified: boolean; passed: number; total: number; flaky?: boolean; at: string };
		}
	}
	return null;
}

export function PlanStatusBadge({ status }: { status: string }) {
	const map: Record<string, { label: string; cls: string }> = {
		proposed: { label: "proposed", cls: "border-warning/40 text-warning-strong" },
		draft: { label: "draft", cls: "border-muted-foreground/40 text-muted-foreground" },
		approved: { label: "approved", cls: "border-primary/40 text-primary" },
		passed: { label: "passed", cls: "border-success/40 text-success-strong" },
		failed: { label: "failed", cls: "border-destructive/40 text-destructive-strong" },
		rejected: { label: "rejected", cls: "border-destructive/40 text-destructive-strong" },
	};
	const cfg = map[status] ?? { label: status, cls: "" };
	return (
		<Badge variant="outline" className={`text-[11px] ${cfg.cls}`}>
			{cfg.label}
		</Badge>
	);
}

/**
 * The one-line verdict header for a stored run: status, counts, duration, and the raw error if the
 * service recorded one. Wraps rather than truncating so it survives a narrow inspector panel.
 */
export function PanelStatusBadgeRow({
	status,
	runId,
	overall,
	passed,
	failed,
	durationMs,
	error,
}: {
	status: string;
	runId: string;
	overall: string;
	passed: number | null;
	failed: number | null;
	durationMs: number | null;
	error: string | null;
}) {
	return (
		<div className="rounded border border-border bg-card/60 p-2">
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
				<PlanStatusBadge status={status} />
				<span className="font-mono text-muted-foreground">run {runId.slice(0, 8)}</span>
				{overall !== status && <span className="text-muted-foreground">{overall}</span>}
				{passed != null && <span className="text-success-strong">{passed} passed</span>}
				{failed != null && failed > 0 && <span className="text-destructive-strong">{failed} failed</span>}
				{durationMs != null && (
					<span className="ml-auto font-mono text-muted-foreground">{durationMs}ms</span>
				)}
			</div>
			{error && (
				<pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap rounded border border-destructive/30 bg-destructive/5 p-1.5 font-mono text-[11px] leading-snug text-destructive-strong/90 [overflow-wrap:anywhere]">
					{error}
				</pre>
			)}
		</div>
	);
}

export function VerificationBadge({
	plan,
	onVerify,
	verifying,
}: {
	plan: TestPlan;
	onVerify?: (planId: string) => void;
	verifying?: boolean;
}) {
	const v = planVerification(plan);
	// min-height keeps the interactive variants at the 24px minimum target size.
	const pill =
		"inline-flex min-h-[24px] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium";

	if (verifying) {
		return (
			<span className={`${pill} bg-warning/15 text-warning-strong`}>
				<Loader2 className="h-3 w-3 animate-spin" /> Verifying…
			</span>
		);
	}
	if (!v) {
		return onVerify ? (
			<button
				type="button"
				onClick={() => onVerify(plan.id)}
				className={`${pill} border border-border text-muted-foreground hover:text-foreground`}
				title="Run this plan headless against the live org to confirm it works before you run it"
			>
				<CheckCircle2 className="h-3 w-3" /> Verify
			</button>
		) : null;
	}
	if (v.verified && !v.flaky) {
		return (
			<span className={`${pill} bg-success/15 text-success-strong`} title="Passed a live pre-flight run">
				<CheckCircle2 className="h-3 w-3" /> Verified
			</span>
		);
	}
	if (v.verified && v.flaky) {
		return (
			<button
				type="button"
				onClick={() => onVerify?.(plan.id)}
				className={`${pill} bg-warning/15 text-warning-strong`}
				title={`Passed ${v.passed}/${v.total} — flaky. Click to re-verify.`}
			>
				<AlertTriangle className="h-3 w-3" /> Flaky {v.passed}/{v.total}
			</button>
		);
	}
	return (
		<button
			type="button"
			onClick={() => onVerify?.(plan.id)}
			className={`${pill} bg-destructive/15 text-destructive-strong`}
			title="The plan failed a live pre-flight run. Refine it in chat, then re-verify."
		>
			<AlertTriangle className="h-3 w-3" /> Needs attention
		</button>
	);
}
